import { VatCondition } from "../../administration"
import { FiscalDocument, FiscalDocumentStatus, FiscalDocumentClass, FiscalDocumentType } from "../state/types"

const uninvoicedFiscalDocumentStatuses: FiscalDocumentStatus[] = [
    FiscalDocumentStatus.PENDING,
    FiscalDocumentStatus.PREVIEW,
    FiscalDocumentStatus.REJECTED
]

// Una operación está sin facturar mientras no tenga comprobante o el comprobante esté pendiente,
// en preview o rechazado. TO_AUTHORIZE y AUTHORIZED ya no habilitan una nueva facturación.
export const isUninvoicedFiscalDocumentStatus = (status?: FiscalDocumentStatus): boolean =>
    !status || uninvoicedFiscalDocumentStatuses.includes(status)

// Un acopio parcialmente facturado todavía admite una factura más; las ventas siguen usando
// isUninvoicedFiscalDocumentStatus para no reabrir una facturación ya iniciada.
export const isInvoiceableFiscalDocumentStatus = (status?: FiscalDocumentStatus): boolean =>
    isUninvoicedFiscalDocumentStatus(status) || status === FiscalDocumentStatus.PARTIALLY_INVOICED

// La factura anulada conserva el estado AUTHORIZED: la anulación es un hecho de negocio
// (compensada por una NC), no un estado fiscal.
export const isAnnulledFiscalDocument = (fiscalDocument?: FiscalDocument): boolean =>
    !!fiscalDocument?.annulledAt

// La NC que anula una factura: se muestra dentro de la factura anulada, no como comprobante
// suelto. Se busca por la marca de anulación; una NC manual nunca se asocia acá.
export const getAnnulmentCreditNote = (
    invoice: FiscalDocument,
    fiscalDocuments: FiscalDocument[]
): FiscalDocument | undefined =>
    fiscalDocuments.find(candidate =>
        candidate.documentType === FiscalDocumentType.CREDIT_NOTE &&
        (candidate.id === invoice.annulmentCreditNoteId ||
            candidate.annulmentOfFiscalDocumentId === invoice.id))

// Una NC manual (autorizada o en vuelo) asociada a la factura ya compensó total o parcialmente su
// saldo: el server rechaza la anulación por el total. Misma regla que invoiceAnnulmentService.
export const hasAssociatedManualCreditNote = (
    invoice: FiscalDocument,
    fiscalDocuments: FiscalDocument[]
): boolean =>
    fiscalDocuments.some(fiscalDocument =>
        fiscalDocument.documentType === FiscalDocumentType.CREDIT_NOTE &&
        !fiscalDocument.annulmentOfFiscalDocumentId &&
        (fiscalDocument.status === FiscalDocumentStatus.AUTHORIZED ||
            fiscalDocument.status === FiscalDocumentStatus.TO_AUTHORIZE) &&
        (!!fiscalDocument.fiscalDocumentParentIds?.includes(invoice.id) ||
            fiscalDocument.associatedFiscalDocumentId === invoice.id)
    )

// Facturas emitidas de una operación: excluye el borrador (PREVIEW) y las anuladas. La factura
// anulada se sigue mostrando en Documentos, pero no es la factura activa ni bloquea volver a
// facturar. La NC de anulación tampoco es una factura.
export const getActiveInvoices = (
    fiscalDocuments: FiscalDocument[]
): FiscalDocument[] =>
    fiscalDocuments.filter(fiscalDocument =>
        fiscalDocument.documentType === FiscalDocumentType.INVOICE &&
        fiscalDocument.status !== FiscalDocumentStatus.PREVIEW &&
        !isAnnulledFiscalDocument(fiscalDocument)
    )

// La factura activa de la operación: la última factura emitida, ignorando el borrador (PREVIEW)
// y las facturas anuladas. Una factura anulada no bloquea volver a facturar.
export const getLatestInvoice = (
    fiscalDocuments: FiscalDocument[]
): FiscalDocument | undefined => {
    const invoices = getActiveInvoices(fiscalDocuments)
        .sort((fiscalDocumentA, fiscalDocumentB) =>
            fiscalDocumentA.issueDate.localeCompare(fiscalDocumentB.issueDate))
    return invoices[invoices.length - 1]
}

// Una operación está facturada cuando tiene una factura activa (no anulada) ya enviada (pendiente de
// autorización) o autorizada. Hasta entonces sus notas de crédito no son fiscales: la factura las descuenta
export const hasSentInvoice = (fiscalDocuments: FiscalDocument[]): boolean =>
    getActiveInvoices(fiscalDocuments).some(invoice => !isUninvoicedFiscalDocumentStatus(invoice.status))

export const fiscalDocumentClass = (
    sellerVatCondition: VatCondition,
    buyerVatCondition: VatCondition
) => {
    const classMap: Map<VatCondition, FiscalDocumentClass> = new Map()
    if (sellerVatCondition === VatCondition.REGISTERED_TAXPAYER) {
        classMap
            .set(VatCondition.REGISTERED_TAXPAYER, FiscalDocumentClass.A)
            .set(VatCondition.EXEMPT, FiscalDocumentClass.B)
            .set(VatCondition.FINAL_CONSUMER, FiscalDocumentClass.B)
            .set(VatCondition.SIMPLIFIED_TAXPAYER, FiscalDocumentClass.A)
            .set(VatCondition.NON_CATEGORIZED, FiscalDocumentClass.B)
            .set(VatCondition.FOREIGN_CLIENT, FiscalDocumentClass.B)
            .set(VatCondition.RELEASED, FiscalDocumentClass.B)
            .set(VatCondition.SOCIAL_SIMPLIFIED_TAXPAYER, FiscalDocumentClass.A)
            .set(VatCondition.NOT_APPLICABLE, FiscalDocumentClass.B)
            .set(VatCondition.PROMOTED_INDEPENDENT_SIMPLIFIED_TAXPAYER, FiscalDocumentClass.A)
    } else if (sellerVatCondition === VatCondition.SIMPLIFIED_TAXPAYER) {
        classMap
            .set(VatCondition.REGISTERED_TAXPAYER, FiscalDocumentClass.C)
            .set(VatCondition.EXEMPT, FiscalDocumentClass.C)
            .set(VatCondition.FINAL_CONSUMER, FiscalDocumentClass.C)
            .set(VatCondition.SIMPLIFIED_TAXPAYER, FiscalDocumentClass.C)
            .set(VatCondition.NON_CATEGORIZED, FiscalDocumentClass.C)
            .set(VatCondition.FOREIGN_CLIENT, FiscalDocumentClass.E)
            .set(VatCondition.RELEASED, FiscalDocumentClass.C)
            .set(VatCondition.SOCIAL_SIMPLIFIED_TAXPAYER, FiscalDocumentClass.C)
            .set(VatCondition.NOT_APPLICABLE, FiscalDocumentClass.C)
            .set(VatCondition.PROMOTED_INDEPENDENT_SIMPLIFIED_TAXPAYER, FiscalDocumentClass.C)
    }

    return classMap.get(buyerVatCondition)!
}
